import React, { useState, useEffect } from 'react';
import './DataAgreementModal.css';

const DATA_TYPES = [
    { id: 'accelerometer', label: 'Accelerometer' },
    { id: 'gyroscope', label: 'Gyroscope' },
    { id: 'heartRate', label: 'Heart Rate' },
    { id: 'stepCount', label: 'Step Count' },
    { id: 'sleep', label: 'Sleep Data' },
    { id: 'gps', label: 'GPS (Anonymised)' },
];

const RETENTION_OPTIONS = [
    { value: '3', label: '3 months' },
    { value: '6', label: '6 months' },
    { value: '12', label: '12 months' },
    { value: '24', label: '24 months' },
];

const DataAgreementModal = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        institutionName: '',
        piName: '',
        piEmail: '',
        piEmailConfirm: '',
        studyReference: '',
        ethicsApprovalRef: '',
        dataTypes: [],
        cohortSize: '',
        retentionPeriod: '12',
        notes: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [agreementId, setAgreementId] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setFormData({
                institutionName: '',
                piName: '',
                piEmail: '',
                piEmailConfirm: '',
                studyReference: '',
                ethicsApprovalRef: '',
                dataTypes: [],
                cohortSize: '',
                retentionPeriod: '12',
                notes: '',
            });
            setIsSubmitting(false);
            setSubmitted(false);
            setAgreementId('');
            setError('');
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }, [isOpen]);

    const handleClose = () => {
        document.body.style.overflow = '';
        onClose();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setError('');
    };

    const handleDataTypeToggle = (typeId) => {
        setFormData(prev => ({
            ...prev,
            dataTypes: prev.dataTypes.includes(typeId)
                ? prev.dataTypes.filter(t => t !== typeId)
                : [...prev.dataTypes, typeId],
        }));
    };

    const validate = () => {
        if (!formData.institutionName.trim()) return 'Institution name is required';
        if (!formData.piName.trim()) return 'Principal Investigator name is required';
        if (!formData.piEmail.trim()) return 'PI email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.piEmail)) return 'Invalid email format';
        if (formData.piEmail !== formData.piEmailConfirm) return 'Email addresses do not match';
        if (!formData.ethicsApprovalRef.trim()) return 'Ethics approval reference is required';
        if (formData.dataTypes.length === 0) return 'Select at least one data type';
        if (!formData.cohortSize || parseInt(formData.cohortSize) < 1) return 'Estimated cohort size is required';
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const response = await fetch('/api/request-data-agreement', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    institutionName: formData.institutionName.trim(),
                    piName: formData.piName.trim(),
                    piEmail: formData.piEmail.trim(),
                    studyReference: formData.studyReference.trim(),
                    ethicsApprovalRef: formData.ethicsApprovalRef.trim(),
                    dataTypes: formData.dataTypes,
                    cohortSize: parseInt(formData.cohortSize),
                    retentionPeriod: parseInt(formData.retentionPeriod),
                    notes: formData.notes.trim(),
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to submit request');
            }

            setAgreementId(result.agreementId);
            setSubmitted(true);
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="da-modal-overlay" onClick={handleClose}>
            <div className="da-modal" onClick={e => e.stopPropagation()}>
                <div className="da-modal-header">
                    <div>
                        <div className="da-modal-title">Request Data Sharing Agreement</div>
                        {!submitted && (
                            <div className="da-modal-step">Complete the form below to generate a data sharing agreement</div>
                        )}
                    </div>
                    <button className="da-modal-close" onClick={handleClose}>&times;</button>
                </div>

                <div className="da-modal-body">
                    {submitted ? (
                        <div className="da-success">
                            <div className="da-success-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                </svg>
                            </div>
                            <h3>Request Submitted</h3>
                            <p>
                                Your data sharing agreement request has been submitted. A pre-filled agreement
                                document will be sent to <strong>{formData.piEmail}</strong> for review and digital signature.
                            </p>
                            <div className="da-success-ref">
                                Reference: <strong>{agreementId}</strong>
                            </div>
                            <p className="da-success-note">
                                Once signed by both parties, bulk data export will be enabled for your study on CMPX.
                                You will receive a confirmation email when the agreement is active.
                            </p>
                            <button className="da-btn da-btn-primary" onClick={handleClose}>
                                Close
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="da-form-section">
                                <h4>Institution & Investigator</h4>
                                <div className="da-form-row">
                                    <label className="da-label">
                                        Institution Name <span className="da-required">*</span>
                                        <input
                                            type="text"
                                            name="institutionName"
                                            value={formData.institutionName}
                                            onChange={handleChange}
                                            placeholder="e.g. University of Oxford"
                                            className="da-input"
                                        />
                                    </label>
                                </div>
                                <div className="da-form-row">
                                    <label className="da-label">
                                        Principal Investigator <span className="da-required">*</span>
                                        <input
                                            type="text"
                                            name="piName"
                                            value={formData.piName}
                                            onChange={handleChange}
                                            placeholder="Full name"
                                            className="da-input"
                                        />
                                    </label>
                                </div>
                                <div className="da-form-row da-form-row--two">
                                    <label className="da-label">
                                        PI Email <span className="da-required">*</span>
                                        <input
                                            type="email"
                                            name="piEmail"
                                            value={formData.piEmail}
                                            onChange={handleChange}
                                            placeholder="pi@institution.ac.uk"
                                            className="da-input"
                                        />
                                    </label>
                                    <label className="da-label">
                                        Confirm Email <span className="da-required">*</span>
                                        <input
                                            type="email"
                                            name="piEmailConfirm"
                                            value={formData.piEmailConfirm}
                                            onChange={handleChange}
                                            placeholder="Confirm email"
                                            className="da-input"
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="da-form-section">
                                <h4>Study & Ethics</h4>
                                <div className="da-form-row da-form-row--two">
                                    <label className="da-label">
                                        Study Reference
                                        <input
                                            type="text"
                                            name="studyReference"
                                            value={formData.studyReference}
                                            onChange={handleChange}
                                            placeholder="Platform slug or external ref"
                                            className="da-input"
                                        />
                                    </label>
                                    <label className="da-label">
                                        Ethics Approval Ref <span className="da-required">*</span>
                                        <input
                                            type="text"
                                            name="ethicsApprovalRef"
                                            value={formData.ethicsApprovalRef}
                                            onChange={handleChange}
                                            placeholder="e.g. IRAS 312456"
                                            className="da-input"
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="da-form-section">
                                <h4>Data Scope</h4>
                                <p className="da-form-hint">Select the data types your study requires access to.</p>
                                <div className="da-data-types">
                                    {DATA_TYPES.map(type => (
                                        <button
                                            key={type.id}
                                            type="button"
                                            className={`da-data-type-btn ${formData.dataTypes.includes(type.id) ? 'da-data-type-btn--active' : ''}`}
                                            onClick={() => handleDataTypeToggle(type.id)}
                                        >
                                            {formData.dataTypes.includes(type.id) && (
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                            )}
                                            {type.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="da-form-row da-form-row--two">
                                    <label className="da-label">
                                        Estimated Cohort Size <span className="da-required">*</span>
                                        <input
                                            type="number"
                                            name="cohortSize"
                                            value={formData.cohortSize}
                                            onChange={handleChange}
                                            placeholder="Number of participants"
                                            min="1"
                                            className="da-input"
                                        />
                                    </label>
                                    <label className="da-label">
                                        Data Retention Period <span className="da-required">*</span>
                                        <select
                                            name="retentionPeriod"
                                            value={formData.retentionPeriod}
                                            onChange={handleChange}
                                            className="da-input"
                                        >
                                            {RETENTION_OPTIONS.map(opt => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            </div>

                            <div className="da-form-section">
                                <h4>Additional Notes</h4>
                                <textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    placeholder="Any specific requirements, data formats, or additional context for your request..."
                                    className="da-textarea"
                                    rows={3}
                                />
                            </div>

                            {error && (
                                <div className="da-error">{error}</div>
                            )}

                            <div className="da-modal-footer">
                                <button type="button" className="da-btn da-btn-secondary" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button type="submit" className="da-btn da-btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DataAgreementModal;
